import { useEffect, useMemo, useRef, useState } from "react";
import type { InterestsState } from "../lib/use-audience-interests";

/**
 * Choosing the audience is the most consequential decision on this page, so it
 * gets a real picker: a search box, every category with the number of people
 * behind it, and a running total of who would receive the issue.
 *
 * It was a bare <select> before, which read as a minor preference rather than
 * the control that decides who gets the newsletter.
 */
export function CategoryPicker({
  selected, catalogue, onChange, onClose,
}: {
  selected: string[];
  /** Fetched once by the panel, so opening the picker costs nothing. */
  catalogue: InterestsState;
  onChange: (next: string[]) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return catalogue.choices;
    return catalogue.choices.filter(
      (c) => c.label.toLowerCase().includes(needle)
        || c.value.toLowerCase().includes(needle)
        || (c.labelAr ?? "").includes(q.trim()),
    );
  }, [q, catalogue.choices]);

  const toggle = (label: string) => {
    onChange(selected.includes(label)
      ? selected.filter((c) => c !== label)
      : [...selected, label]);
  };

  return (
    <div className="nl-catpicker ui-enter" ref={boxRef} role="dialog" aria-label="Select categories">
      <input
        ref={searchRef}
        className="nl-input nl-catpicker-search"
        type="search"
        placeholder="Search categories…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <ul className="nl-catpicker-list">
        {shown.length === 0 && (
          <li className="nl-catpicker-none">
            {catalogue.status === "loading" ? "Loading categories…"
              : q.trim() ? `No category matches “${q.trim()}”.`
              : catalogue.status === "error" ? "No categories could be loaded."
              : "No categories to choose from."}
          </li>
        )}
        {shown.map((c) => {
          const on = selected.includes(c.value);
          return (
            <li key={c.value}>
              <button
                type="button"
                className="nl-catpicker-row"
                aria-pressed={on}
                onClick={() => toggle(c.value)}
              >
                <span className="nl-catpicker-box" aria-hidden>
                  <svg viewBox="0 0 12 12" width="12" height="12">
                    <path d="M2.5 6.2 L4.8 8.5 L9.5 3.6" fill="none" stroke="currentColor"
                      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="nl-catpicker-names">
                  <span className="nl-catpicker-ar">{c.labelAr ?? c.label}</span>
                  {c.labelAr && <span className="nl-catpicker-en">{c.label}</span>}
                </span>
                {/* Subscribers who chose this topic — counted from
                    newsletter.interests, not estimated. */}
                <span className="nl-catpicker-n" title="Subscribers who chose this topic">
                  {c.subscribers.toLocaleString()}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {catalogue.status === "error" && (
        <p className="nl-catpicker-warn">
          {catalogue.message} Nothing is listed rather than offering topics no
          subscriber has chosen.
          {" "}
          <button type="button" className="nl-text-btn" onClick={catalogue.retry}>
            Try again
          </button>
        </p>
      )}
      {catalogue.status === "ready" && catalogue.choices.length === 0 && (
        <p className="nl-catpicker-warn">
          No subscriber has chosen a topic yet, so there is no audience to send to.
        </p>
      )}

      <div className="nl-catpicker-foot">
        <span>
          {selected.length === 0
            ? "Nothing selected"
            : `${selected.length} selected`}
        </span>
        <button type="button" className="nl-picker-add is-primary" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}
